import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Screen from '../../components/common/Screen';
import NavBar from '../../components/common/NavBar';
import Icon from '../../components/common/Icon';
import Back from '../../components/common/Back';
import YouTubeEmbed from '../../components/elements/YoutubeEmbed';
import RolePickerModal from './RolePickerModal';
import TeamPaymentBanner, { useTeamPaymentLock } from '../../components/team/TeamPaymentLock';
import BadgeSheet, { BadgeTile } from '../../components/common/BadgeSheet';
import { apiRequest, awardArt, teamPhoto } from '../../utils';

import '../user/PublicProfile.css';
import './PublicTeam.css';

const companyFallback = require('./../../assets/images/fallback/company.png');
const userFallbackAsset = require('./../../assets/images/fallback/user.png');
// Project default (branding) wins over the bundled placeholder — read lazily
// so a branding refresh in this tab is picked up on the next render.
const userFallback = () =>
  localStorage.getItem('defaultPlayerPhoto') || userFallbackAsset;
const activityFallback = require('./../../assets/images/fallback/activity.png');

/* A prize's picture is decided in ONE place for every screen that draws one (`awardArt` in
   utils): what was uploaded with the award, else our bundled art by name, else the cup. */

// NavBar self-hides until the viewer is logged in (see NavBar.js), so it's
// safe to render here unconditionally. The Back arrow is for a logged-in visitor only (they
// came here from inside the app — a list, the home, a chip); history first, /home on a cold open.
const Layout = ({ children, authed }) => (
  <Screen>
    {authed && <Back to="/home" />}
    <div className="top">{children}</div>
    <div className="bottom">
      <NavBar />
    </div>
  </Screen>
);

// "now < end" with sloppy parsing-tolerant Date.
const isLive = (start, end) => {
  if (!start || !end) return false;
  const now = new Date();
  return new Date(start) <= now && now < new Date(end);
};

const Page = () => {
  const { t } = useTranslation(['common', 'awards', 'roles']);
  const navigate = useNavigate();
  const { teamId } = useParams();

  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading'); // 'loading' | 'ok' | 'notfound'
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [showRolePicker, setShowRolePicker] = useState(false);
  const [openBadge, setOpenBadge] = useState(null);

  // ESC closes the lightbox and the body scroll-lock.
  useEffect(() => {
    if (!lightboxSrc) return;
    const onKey = (e) => { if (e.key === 'Escape') setLightboxSrc(null); };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [lightboxSrc]);

  const reload = () => {
    if (!teamId) return;
    apiRequest('GET', `/public-team/${teamId}`)
      .then((res) => { setData(res); setStatus('ok'); })
      .catch(() => { setStatus('notfound'); });
  };

  useEffect(() => {
    if (!teamId) { setStatus('notfound'); return; }
    let cancelled = false;
    setStatus('loading');
    apiRequest('GET', `/public-team/${teamId}`)
      .then((res) => {
        if (cancelled) return;
        // Merged-away team: refresh the JWT from the live Player row and land
        // on the surviving team, so old links (and stale tokens) keep working.
        if (res?.merged_into) {
          apiRequest('POST', '/refresh-team', {}).catch(() => {});
          navigate(`/public-team/${res.merged_into}`, { replace: true });
          return;
        }
        setData(res); setStatus('ok');
      })
      .catch(() => { if (!cancelled) setStatus('notfound'); });
    return () => { cancelled = true; };
  }, [teamId, navigate]);

  // Own team: surface pending merge requests + the entry point to the merge flow.
  const [mergeInfo, setMergeInfo] = useState(null);
  const isOwnTeam = !!data?.viewer?.is_own_team;
  useEffect(() => {
    if (!isOwnTeam) { setMergeInfo(null); return; }
    apiRequest('GET', '/merge-requests').then(setMergeInfo).catch(() => {});
  }, [isOwnTeam, teamId]);

  // NOTE: inviting (open seats + buy-a-seat link) deliberately does NOT live
  // here — the public team page is how a team looks to the outside world.
  // The invite block sits on Home and on the Players screen.

  const team   = data?.team;
  const viewer = data?.viewer;
  // Auth state is derived from the same response — null while loading,
  // true/false after, so the NavBar appears only once we know.
  const authed = data && typeof data.is_authenticated === 'boolean' ? data.is_authenticated : null;

  // Project default (branding) wins over the bundled placeholder.
  const heroPhoto = teamPhoto(team?.picture, team?.company_picture);
  const heroIsCompanyLogo = !team?.picture && !!team?.company_picture;
  // Voting is only "enabled" for an event if the organiser configured a
  // CheerAlbum — without it cheers_start_time/end_time stay null. The
  // leaderboard query still returns every team (outer-joined cheer counts),
  // so without this gate we'd show "#N — 0 cheers" rank cards on events that
  // never had voting at all.
  const votingEnabled = !!(team?.cheers_start_time || team?.cheers_end_time);
  const cheersOpen = isLive(team?.cheers_start_time, team?.cheers_end_time);
  const eventOpen  = team?.closing_date ? new Date() < new Date(team.closing_date) : true;
  // Challenges close when the event starts (mirrors create_rival); the closing
  // date only stands in when there is no start date. Joker and roster keep the
  // closing date. Only a fully date-less event stays open.
  const challengeCutoff = team?.event_date || team?.closing_date || team?.event_end_date;
  const challengesOpen = challengeCutoff ? new Date() < new Date(challengeCutoff) : true;
  const isOtherPlayer = !!viewer && viewer.role === 'player' && !viewer.is_own_team;
  // Cross-edition lens widens browsing to sibling events, but challenges are
  // a per-event mechanic. Block only when the backend explicitly says the
  // events differ; an undefined flag (older backend) falls back to the
  // pre-edition behaviour so we don't accidentally hide the action.
  const crossEventBlocked = isOtherPlayer && !!viewer.user_team_id && viewer.is_same_event === false;
  // Challenges are a competitive-"Event" mechanic. Events that keep event_type
  // 'Event' but leave max_challenge_others at 0 have no rivalry — gate on it too,
  // otherwise the button shows and the backend refuses.
  const canChallenge = isOtherPlayer && !crossEventBlocked && challengesOpen
    && team?.event_type === 'Event' && team?.max_challenge_others > 0;
  const recapEnd = team?.event_end_date || team?.event_date;
  const recapIsWrapUp = !!recapEnd && new Date(recapEnd) < new Date();
  const canEdit = !!viewer && (viewer.is_own_team || (viewer.role === 'contactPerson' && viewer.user_company_id === team?.company_id));
  // Admin-side toggle from Machina. Default to true when the payload
  // is missing the field (older backend) so we don't accidentally lock
  // every event when the column hasn't been deployed yet.
  const playerModificationAllowed = team?.can_modify_players !== false;
  // A team that exists only because of an online payment that failed waits for it: the
  // banner offers "Pay now" and the edit/manage/merge entries stay shut until then.
  const payLock = useTeamPaymentLock({ teamId, enabled: canEdit });
  const canManagePlayers = canEdit && eventOpen && playerModificationAllowed && !payLock;

  // Cheer slot in the action bar: an own-team player shares their team for
  // cheers; anyone else votes. A contact person of the team's OWN company gets
  // neither — voting for your own team isn't allowed, and the "Share for cheers"
  // page is tied to the viewer's own player team (JWT), not this one.
  const contactOwnsTeam = !!viewer && viewer.role === 'contactPerson'
    && viewer.user_company_id === team?.company_id;
  const canShareCheers = cheersOpen && !!team?.picture && !!viewer?.is_own_team;
  const canVote = cheersOpen && !!team?.picture && !viewer?.is_own_team && !contactOwnsTeam;
  const showCheerAction = canShareCheers || canVote;

  // "Hercules Trophy 21/06" — day/month suffix on event chips. No year.
  const formatEventDay = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
  };
  const eventDayLabel = formatEventDay(team?.event_date);

  const handleChallenge = async (challenged) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await fetch('/api/put-challenge', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ challenged_id: teamId, challenged }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.message || 'somethingWentWrong');
      }
      setToast(challenged ? t('challengedToastSent', { defaultValue: 'Challenge sent' }) : t('challengeRemoved', { defaultValue: 'Challenge removed' }));
      reload();
    } catch (e) {
      setToast(t(e.message, { defaultValue: t('somethingWentWrong') }));
    } finally {
      setBusy(false);
      setTimeout(() => setToast(null), 2200);
    }
  };

  // ---------- LOADING ----------
  if (status === 'loading') {
    return (
      <Layout authed={authed}>
        <div className="pp-page">
          <div className="pp-skeleton pp-skel-hero" />
          <div className="pp-skeleton pp-skel-stats" />
          <div className="pp-skeleton pp-skel-row" />
        </div>
      </Layout>
    );
  }

  if (status === 'notfound' || !data) {
    return (
      <Layout authed={authed}>
        <div className="pp-page">
          <div className="pp-empty">
            <div className="pp-empty-icon"><Icon name="info" /></div>
            <div className="pp-empty-title">{t('publicTeamNotFound', { defaultValue: 'Team not found' })}</div>
          </div>
        </div>
      </Layout>
    );
  }

  const { lineup = [], awards = [], submissions = [], social_submissions = [], joker, rivals, points, total_votes, rank, total_teams, vote_rank, recent_cheers = [] } = data;
  const hasRivals = (rivals?.challenged?.length || 0) + (rivals?.challengers?.length || 0) > 0;
  const voteCount = team?.cheer_count ?? total_votes ?? 0;
  const hasMoreCheers = voteCount > recent_cheers.length;
  const totalPoints = Math.round(Number(points?.points) || 0);
  const showVoteRank = votingEnabled && vote_rank != null;
  const showRankCards = rank != null || showVoteRank;

  return (
    <Layout authed={authed}>
      <div className="pp-page">

        {/* HERO */}
        <section className={`pp-hero ${authed ? 'pt-hero--with-back' : ''}`}>
          <div className="pp-hero-bg" style={{ backgroundImage: `url(${heroPhoto})` }} />

          {/* Top-left house badge */}
          {team?.house_id && (
            <span className="pt-house" style={{ color: team?.house_color || '#fff' }} title={t('house', { defaultValue: 'House' })}>
              <Icon name="house-flag" style={{ color: team?.house_color || '#fff' }} />
            </span>
          )}

          {/* Top-right action — settings (gear) for own/company team */}
          {canEdit && !payLock && (
            <div className="pt-hero-actions">
              <Link to="/edit-team" className="pt-hero-action" title={t('edit', { defaultValue: 'Edit' })}
                    onClick={() => sessionStorage.setItem('otherTeamId', teamId)}>
                <Icon name="settings" />
              </Link>
            </div>
          )}

          <div className="pp-hero-inner">
            <button
              type="button"
              className="pp-avatar-ring pt-avatar-btn"
              onClick={() => setLightboxSrc(heroPhoto)}
              aria-label={t('viewPicture', { defaultValue: 'View picture' })}
            >
              <img
                src={heroPhoto}
                alt=""
                className={`pp-avatar ${heroIsCompanyLogo ? 'pt-avatar-contain' : ''}`}
              />
            </button>

            <h1 className="pp-name">{team.name}</h1>
            {team.slogan && <p className="pp-bio"><i>{team.slogan}</i></p>}

            <div className="pt-chips">
              {team.team_number && (
                <span className="pt-chip"><span className="pt-chip-text">#{team.team_number}</span></span>
              )}
              {team.company_id && (
                <Link to={`/public-company/${team.company_id}`} className="pt-chip">
                  <img src={team.company_picture || companyFallback} alt="" className="pt-chip-logo" />
                  <span className="pt-chip-text">{team.company_name}</span>
                </Link>
              )}
              {/* The community this team was booked through — an extra chip BESIDE the
                  team's own identity, never instead of it. A team booked with a code has no
                  company, so the two chips normally don't compete. They DO on a community
                  founded through the old storefront flow, which owns its teams: there
                  company and community are the same partner, and rendering both would print
                  the same name twice. */}
              {team.community_id && team.community_name
                && String(team.community_id).toLowerCase() !== String(team.company_id || '').toLowerCase() && (
                <Link to={`/public-company/${team.community_id}`} className="pt-chip">
                  <span className="pt-chip-text">
                    {t('viaCommunity', { community: team.community_name })}
                  </span>
                </Link>
              )}
              {team.event_name && (
                team.event_id ? (
                  <Link to={`/public-event/${team.event_id}`} className="pt-chip">
                    {team.event_picture && (
                      <img src={team.event_picture} alt="" className="pt-chip-logo" />
                    )}
                    <span className="pt-chip-text">
                      {team.event_name}{eventDayLabel ? ` ${eventDayLabel}` : ''}
                    </span>
                  </Link>
                ) : (
                  <span className="pt-chip">
                    {team.event_picture && (
                      <img src={team.event_picture} alt="" className="pt-chip-logo" />
                    )}
                    <span className="pt-chip-text">
                      {team.event_name}{eventDayLabel ? ` ${eventDayLabel}` : ''}
                    </span>
                  </span>
                )
              )}
            </div>
          </div>
        </section>

        <TeamPaymentBanner lock={payLock} className="mx-3 mt-3" />

        {/* RECAP — the team's event as a story with music. Public like this page; after the
            event it is the headline action, before it a "meet the team". */}
        <div className="pt-actions">
          <Link className={`pt-action ${recapIsWrapUp ? 'pt-action-primary' : 'pt-action-soft'}`}
                to={`/public-team/${teamId}/recap`}>
            <Icon name="play" pack="sr" /> {recapIsWrapUp ? t('recapWatch') : t('recapWatchBefore')}
          </Link>
        </div>

        {/* MERGE — own team only: pending requests + the entry to the merge flow */}
        {isOwnTeam && !payLock && mergeInfo && (mergeInfo.open || mergeInfo.incoming?.length > 0 || mergeInfo.outgoing?.length > 0) && (
          <div className="pt-actions">
            <Link className={`pt-action ${mergeInfo.incoming?.length ? 'pt-action-active' : 'pt-action-soft'}`} to="/merge-teams">
              <Icon name="users" /> {t('mergeTeams')}
              {mergeInfo.incoming?.length ? ` (${mergeInfo.incoming.length})` : ''}
            </Link>
          </div>
        )}

        {/* ACTION BAR — the WhatsApp group join moved to the home screen (under the
            countdown); this bar is now challenge + voting actions only. */}
        {(canChallenge || (crossEventBlocked && eventOpen && team?.event_type === 'Event') || showCheerAction) && (
          <div className="pt-actions">
            {/* Challenge / Unchallenge */}
            {canChallenge && (
              viewer.is_challenging ? (
                <button className="pt-action pt-action-active" onClick={() => handleChallenge(false)} disabled={busy}>
                  <Icon name="megaphone" /> {t('unchallengeOther', { defaultValue: 'Remove challenge' })}
                </button>
              ) : (
                <button className="pt-action pt-action-soft" onClick={() => handleChallenge(true)} disabled={busy}>
                  <Icon name="megaphone" /> {t('challengeOther', { defaultValue: 'Challenge this team' })}
                </button>
              )
            )}

            {/* Cross-edition: explain the missing challenge action without
                an interactive button. Same edition, different event day. */}
            {!canChallenge && crossEventBlocked && eventOpen && team?.event_type === 'Event' && (
              <span
                className="pt-action pt-action-soft"
                aria-disabled="true"
                style={{ opacity: 0.6, cursor: 'not-allowed' }}
                title={t('crossEditionChallengeHint', { defaultValue: "This team is in another event of your edition — challenges only work within your own event." })}
              >
                <Icon name="megaphone" /> {t('crossEditionCantChallenge', { defaultValue: "Can't challenge — different event" })}
              </span>
            )}

            {/* Own-team player: share for cheers. Outsider: vote. A contact
                person of the team's own company gets neither (see canVote). */}
            {canShareCheers && (
              <Link className="pt-action pt-action-primary" to="/cheers">
                <Icon name="share-square" /> {t('shareForCheers', { defaultValue: 'Share for cheers' })}
              </Link>
            )}
            {canVote && (
              <Link className="pt-action pt-action-primary" to={`/public-team/${teamId}/vote`}>
                <Icon name="party-horn" pack="sr" /> {t('vote', { defaultValue: 'Vote' })}
              </Link>
            )}

          </div>
        )}

        {/* What "Challenge this team" means — a short nudge under the action bar. */}
        {canChallenge && !viewer.is_challenging && (
          <div style={{ fontSize: '0.8rem', color: '#6c757d', textAlign: 'center', marginTop: '-0.15rem', marginBottom: '0.5rem' }}>
            {t('challengeHint', { defaultValue: 'Finish higher than this team in the provisional final ranking and your team earns an extra point — or the other way around.' })}
          </div>
        )}

        {/* STATS — only if there are points to show */}
        {points && (Number(points.points) > 0 || Number(points.battle_points) > 0 || Number(points.extra_points) > 0) && (
          <div className="pp-stats">
            {authed ? (
              <Link to="/leaderboard" className="pp-stat" style={{ textDecoration: 'none', color: 'inherit' }}>
                <div className="pp-stat-value">{points.points || 0}</div>
                <div className="pp-stat-label">{t('points')}</div>
              </Link>
            ) : (
              <div className="pp-stat">
                <div className="pp-stat-value">{points.points || 0}</div>
                <div className="pp-stat-label">{t('points')}</div>
              </div>
            )}
            <div className="pp-stat">
              <div className="pp-stat-value">{points.battle_points || 0}</div>
              <div className="pp-stat-label">{t('challenges')}</div>
            </div>
            <div className="pp-stat">
              <div className="pp-stat-value">{points.extra_points || 0}</div>
              <div className="pp-stat-label">{t('extra')}</div>
            </div>
          </div>
        )}

        {/* JOKER — intentionally not a link */}
        {joker && joker.id && joker.name && (
          <section className="pp-section">
            <div className="pt-joker">
              <img src={joker.photo_url || activityFallback} alt="" className="pt-joker-img" />
              <div className="pt-joker-fade" />
              <span className="pt-joker-eyebrow"><Icon name="star" pack="sr" /> {t('joker')}</span>
              <div className="pt-joker-name">{joker.name}</div>
            </div>
          </section>
        )}

        {/* AWARDS */}
        {awards.length > 0 && (
          <section className="pp-section">
            <header className="pp-section-header">
              <h2 className="pp-section-title">{t('awards')}</h2>
              <span className="pp-section-count">{awards.length}</span>
            </header>
            <div className="pp-awards-grid">
              {awards.map((aw, i) => {
                /* ★ Reads both shapes. A prize used to arrive as a bare NAME and now arrives as
                   {name, image}; a phone holding a cached bundle would otherwise draw a row of
                   blank tiles the day this ships. */
                const name = typeof aw === 'string' ? aw : (aw || {}).name || '';
                const image = typeof aw === 'string' ? null : (aw || {}).image;
                return (
                  <div key={`${name}-${i}`} className="pp-award" style={{ cursor: 'default' }}>
                    <div className="pp-award-img-wrap">
                      <img src={awardArt(name, image)} alt="" className="pp-award-img" />
                    </div>
                    <div className="pp-award-name">
                      {t(name, { ns: 'awards', defaultValue: name.replace(/(?!^)([A-Z])/g, ' $1') })}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* BADGES — every badge somebody on the team holds, once, rarest first, with how many of
            them hold it. A tap says what it stands for and who on the team has it. */}
        {data.badges?.length > 0 && (
          <section className="pp-section">
            <header className="pp-section-header">
              <h2 className="pp-section-title">{t('badges')}</h2>
              <span className="pp-section-count">{data.badges.length}</span>
            </header>
            <div className="pp-awards-grid">
              {data.badges.map((b) => (
                <BadgeTile key={b.id} badge={b} onOpen={setOpenBadge} showCount />
              ))}
            </div>
          </section>
        )}

        {/* LINEUP */}
        {(lineup.length > 0 || canManagePlayers) && (
          <section className="pp-section">
            <header className="pp-section-header">
              <h2 className="pp-section-title">{t('lineup', { defaultValue: 'Lineup' })}</h2>
              <div className="pt-section-meta">
                {lineup.length > 0 && <span className="pp-section-count">{lineup.length}</span>}
                {canManagePlayers && (
                  <Link
                    to="/players"
                    className="pt-section-action"
                    onClick={() => sessionStorage.setItem('otherTeamId', teamId)}
                  >
                    <Icon name="settings" /> {t('manage')}
                  </Link>
                )}
              </div>
            </header>
            {lineup.length > 0 && (
              <div className="pt-lineup-grid">
                {lineup.map((p) => {
                  // Role-picking is a player-only mechanic. A contact person can
                  // be is_self in their own company's lineup, but /set-own-role
                  // isn't meant for them — hide the picker rather than let them
                  // tap an action that does nothing. They must switch to player.
                  const canPick = p.is_self && authed && viewer?.role === 'player';
                  const roleColor = p.role_color || '#64748B';
                  const openPicker = (e) => { e.preventDefault(); e.stopPropagation(); setShowRolePicker(true); };
                  return (
                    <Link key={p.id} to={`/u/${p.id}`} className="pt-player">
                      <div className="pt-player-img-wrap">
                        <img src={p.photo_url || userFallback()} alt="" className="pt-player-img" />
                        {p.is_captain && <span className="pt-player-captain"><Icon name="user-crown" pack="sr" /></span>}
                      </div>
                      <div className="pt-player-name">{p.full_name}</div>
                      {p.role_id ? (
                        <span
                          className={`pt-player-role${canPick ? ' is-editable' : ''}`}
                          style={{ backgroundColor: `${roleColor}1F`, borderColor: roleColor, color: roleColor }}
                          onClick={canPick ? openPicker : undefined}
                        >
                          <span className="pt-player-role-emoji">{p.role_emoji || '⭐'}</span>
                          {p.role_translation_key
                            ? t(`${p.role_translation_key}.name`, { ns: 'roles', defaultValue: p.role_name })
                            : p.role_name}
                        </span>
                      ) : canPick ? (
                        <button type="button" className="pt-player-role pt-player-role--pick" onClick={openPicker}>
                          <Icon name="plus" /> {t('pickYourRole', { defaultValue: 'Pick your role' })}
                        </button>
                      ) : null}
                    </Link>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {/* RANKING — points + cheers leaderboard positions */}
        {showRankCards && (
          <section className="pp-section">
            <header className="pp-section-header">
              <h2 className="pp-section-title">{t('ranking', { defaultValue: 'Ranking' })}</h2>
            </header>
            <div className="pt-rank-stack">
              {rank != null && (() => {
                const RankWrapper = authed ? Link : 'div';
                const wrapperProps = authed ? { to: '/leaderboard' } : {};
                return (
                  <RankWrapper className="pt-rank pt-rank--points" {...wrapperProps}>
                    <div className="pt-rank__pos">
                      <span className="pt-rank__pos-num">
                        <span className="pt-rank__hash">#</span>{rank}
                      </span>
                      {total_teams && (
                        <span className="pt-rank__pos-of">
                          {t('of', { defaultValue: 'of' })} {total_teams}
                        </span>
                      )}
                    </div>
                    <div className="pt-rank__body">
                      <div className="pt-rank__kicker">{t('leaderboard')}</div>
                      <div className="pt-rank__metric">
                        <strong>{totalPoints.toLocaleString()}</strong> {t('points')}
                      </div>
                    </div>
                    <span className="pt-rank__icon">
                      <Icon name="ranking-star" />
                    </span>
                  </RankWrapper>
                );
              })()}

              {showVoteRank && (() => {
                const VoteWrapper = authed ? Link : 'div';
                const wrapperProps = authed ? { to: '/cheers-leaderboard' } : {};
                return (
                  <VoteWrapper className="pt-rank pt-rank--votes" {...wrapperProps}>
                    <div className="pt-rank__pos">
                      <span className="pt-rank__pos-num">
                        <span className="pt-rank__hash">#</span>{vote_rank}
                      </span>
                      {total_teams && (
                        <span className="pt-rank__pos-of">
                          {t('of', { defaultValue: 'of' })} {total_teams}
                        </span>
                      )}
                    </div>
                    <div className="pt-rank__body">
                      <div className="pt-rank__kicker">{t('cheers')}</div>
                      <div className="pt-rank__metric">
                        <strong>{voteCount}</strong> {t('cheers')}
                      </div>
                    </div>
                    <span className="pt-rank__icon">
                      <Icon name="party-horn" pack="sr" />
                    </span>
                  </VoteWrapper>
                );
              })()}
            </div>
          </section>
        )}

        {/* MISSION SUBMISSIONS — only completed (filtered server-side) */}
        {submissions.length > 0 && (
          <section className="pp-section">
            <header className="pp-section-header">
              <h2 className="pp-section-title">{t('missions')}</h2>
              <span className="pp-section-count">{submissions.length}</span>
            </header>
            <div className="pt-mission-grid">
              {submissions.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  className="pt-mission"
                  onClick={() => setLightboxSrc(s.image_url)}
                >
                  <img src={s.image_url} alt="" className="pt-mission-img" />
                  <div className="pt-mission-fade" />
                  <div className="pt-mission-name">{s.name}</div>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* SOCIAL POSTS — only completed (filtered server-side) */}
        {social_submissions.length > 0 && (
          <section className="pp-section">
            <header className="pp-section-header">
              <h2 className="pp-section-title">{t('socialPosts', { defaultValue: 'Social posts' })}</h2>
              <span className="pp-section-count">{social_submissions.length}</span>
            </header>
            <div className="pt-social-list">
              {social_submissions.map((s) => (
                <a key={s.id} className="pt-social" href={s.social_media_url} target="_blank" rel="noopener noreferrer">
                  <img src={s.image_url || activityFallback} alt="" className="pt-social-img" />
                  <div className="pt-social-meta">
                    <p className="pt-social-name">{s.name}</p>
                    {s.caption && <p className="pt-social-caption">{s.caption}</p>}
                  </div>
                  <Icon name="arrow-up-right-from-square" />
                </a>
              ))}
            </div>
          </section>
        )}

        {/* RIVALS — only if any */}
        {hasRivals && (
          <section className="pp-section">
            <header className="pp-section-header">
              <h2 className="pp-section-title">{t('rivals', { defaultValue: 'Rivals' })}</h2>
              <span className="pp-section-count">{(rivals.challenged?.length || 0) + (rivals.challengers?.length || 0)}</span>
            </header>
            <div className="pt-rivals-row">
              {(rivals.challenged || []).map((r) => (
                <Link key={`c-${r.id}`} to={`/public-team/${r.id}`} className="pt-rival">
                  <img src={teamPhoto(r.photo_url, r.company_picture)} alt="" className="pt-rival-img" />
                  <span>{r.name}</span>
                </Link>
              ))}
              {(rivals.challengers || []).map((r) => (
                <Link key={`r-${r.id}`} to={`/public-team/${r.id}`} className="pt-rival">
                  <img src={teamPhoto(r.photo_url, r.company_picture)} alt="" className="pt-rival-img" />
                  <span>{r.name}</span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* BIO */}
        {team.bio && (
          <section className="pp-section">
            <header className="pp-section-header">
              <h2 className="pp-section-title">{t('bio')}</h2>
            </header>
            <div className="pt-prose">{team.bio}</div>
          </section>
        )}

        {/* SONG */}
        {team.song_url && (
          <section className="pp-section">
            <header className="pp-section-header">
              <h2 className="pp-section-title">{t('song')}</h2>
            </header>
            <div className="pt-song">
              <YouTubeEmbed url={team.song_url} />
            </div>
          </section>
        )}

        {/* VOTES / CHEERS — kept last on purpose; the rest of the page is the
            team's own showcase, this is what other people say about them.
            Gated on votingEnabled so events without a CheerAlbum never show
            a cheers section, even if stray Cheer rows exist. */}
        {votingEnabled && recent_cheers.length > 0 && (
          <section className="pp-section">
            <header className="pp-section-header">
              <h2 className="pp-section-title">{t('cheers')}</h2>
              <div className="pt-section-meta">
                <span className="pp-section-count">{voteCount}</span>
                {hasMoreCheers && (
                  <Link to={`/public-team/${teamId}/cheers`} className="pt-section-link">
                    {t('seeAll')} <Icon name="angle-right" />
                  </Link>
                )}
              </div>
            </header>
            <div className="pt-cheer-list">
              {recent_cheers.map((c) => (
                <div key={c.id} className="pt-cheer">
                  <img
                    src={c.photo_url || userFallback()}
                    alt=""
                    className="pt-cheer-avatar"
                  />
                  <div className="pt-cheer-body">
                    <div className="pt-cheer-name">{c.name}</div>
                    {c.message && <p className="pt-cheer-message">{c.message}</p>}
                  </div>
                </div>
              ))}
            </div>
            {hasMoreCheers && (
              <Link to={`/public-team/${teamId}/cheers`} className="pt-cheer-all-cta">
                {t('seeAll')} <Icon name="angle-right" />
              </Link>
            )}
          </section>
        )}

      </div>

      {toast && <div className="pt-toast">{toast}</div>}

      <BadgeSheet badge={openBadge} onClose={() => setOpenBadge(null)} />

      {showRolePicker && (
        <RolePickerModal
          teamId={teamId}
          onClose={() => setShowRolePicker(false)}
          onChanged={() => { setToast(t('roleUpdated', { defaultValue: 'Role updated' })); setTimeout(() => setToast(null), 2200); reload(); }}
        />
      )}

      {lightboxSrc && (
        <div
          className="pt-lightbox"
          onClick={() => setLightboxSrc(null)}
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            className="pt-lightbox__close"
            onClick={(e) => { e.stopPropagation(); setLightboxSrc(null); }}
            aria-label={t('close', { defaultValue: 'Close' })}
          >
            <Icon name="cross" />
          </button>
          <img
            src={lightboxSrc}
            alt=""
            className="pt-lightbox__img"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </Layout>
  );
};

export default Page;
